/**
 * Resolves EPC CA approval/source deep links to the contract workspace tab.
 * Company-scoped get-by-id APIs → 404 / empty for cross-company (no disclosure).
 */
import React, { useEffect, useState } from 'react';
import { Alert, Spin, Typography } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { epcContractAdminService } from '@/services/epcContractAdmin.service';
import {
  epcCaWorkspaceDeepLink,
  isEpcCaEntityType,
  type EpcCaEntityType,
} from '@/pages/epc/commercial/epcCaDeepLinks';

const { Text } = Typography;

async function loadContractId(entityType: EpcCaEntityType, entityId: string): Promise<string> {
  let row: Record<string, unknown>;
  switch (entityType) {
    case 'epc_payment_application':
      row = await epcContractAdminService.getPaymentApplication(entityId);
      break;
    case 'epc_variation':
      row = await epcContractAdminService.getVariation(entityId);
      break;
    case 'epc_claim':
      row = await epcContractAdminService.getClaim(entityId);
      break;
    case 'epc_eot':
      row = await epcContractAdminService.getEotRequest(entityId);
      break;
    case 'epc_final_account':
      row = await epcContractAdminService.getFinalAccount(entityId);
      break;
    case 'epc_contract_closeout':
      row = await epcContractAdminService.getCloseout(entityId);
      break;
    default:
      throw new Error('Unsupported entity type');
  }
  const contractId = String(row.contract_id || row.contractId || '');
  if (!contractId) throw new Error('Document has no contract reference');
  return contractId;
}

const EpcCaDocumentBridgePage: React.FC = () => {
  const { entityType = '', entityId = '' } = useParams<{
    entityType: string;
    entityId: string;
  }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isEpcCaEntityType(entityType) || !entityId) {
        setError('Not found');
        return;
      }
      try {
        const contractId = await loadContractId(entityType, entityId);
        if (cancelled) return;
        navigate(epcCaWorkspaceDeepLink(contractId, entityType, entityId), { replace: true });
      } catch {
        if (!cancelled) setError('Not found');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [entityType, entityId, navigate]);

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="error"
          showIcon
          message={error}
          description={<Text type="secondary">Document is not available.</Text>}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 48, textAlign: 'center' }}>
      <Spin size="large" />
      <div style={{ marginTop: 16 }}>
        <Text type="secondary">Opening contract document…</Text>
      </div>
    </div>
  );
};

export default EpcCaDocumentBridgePage;
